import { Pencil, Trash } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { type FieldErrors, useFormContext } from 'react-hook-form'

import { useEmployer } from 'apps/employer_renewals/providers/EmployerProvider'
import { QuoteExplorerMode } from 'components/quote_explorer/QuoteExplorer'
import { getFullName, SlabStateDropdown, USStates } from 'components/reusable/form/StateDropdown'
import { Button, ButtonGroup, CollapsibleContainer, IconButton } from 'slab'

import AllowanceClassHeader from './AllowanceClassHeader'
import AllowanceInputBlock from './AllowanceInputBlock'
import { type Allowance, type Allowances } from './FlatAllowanceExplorer'

type Props = {
  index: number
  defaultOpen?: boolean
  mode?: QuoteExplorerMode
  onAdd: () => void
  onRemove: () => void
  validateEmployeeAllowance?: (value: number, allowance: Allowance) => string | undefined
  bypassEmployeeAllownaceErrorForALEs?: boolean
}

const ACCEPTABLE_ALE_ERRORS_REGEX = /an allowance below \$[\d,.]+ may be unaffordable for some employees/i

function AllowanceClass ({
  index,
  defaultOpen = true,
  mode = QuoteExplorerMode.edit,
  onAdd,
  onRemove,
  validateEmployeeAllowance,
  bypassEmployeeAllownaceErrorForALEs = false,
}: Props) {
  const { register, watch, trigger, formState: { errors, submitCount, isSubmitting } } = useFormContext<Allowances>()
  const { employerIsALE } = useEmployer()

  const [isOpen, setIsOpen] = useState(defaultOpen)
  const isExploreMode = mode === QuoteExplorerMode.explore

  const allowance = watch(`allowances.${index}`)

  const allowances = watch('allowances')

  const stateSelectOptions = useMemo(() => {
    return USStates.map((state) => ({
      ...state,
      disabled: allowances.some((allowance) => allowance.class?.location_id === state.value),
    }))
  }, [allowances])

  useEffect(() => {
    if (errors.allowances?.[index]) {
      setIsOpen(true)
    }
  }, [errors.allowances?.[index], submitCount])

  const isAcceptableError = useCallback((errors: FieldErrors<Allowances>) => {
    if (employerIsALE || !bypassEmployeeAllownaceErrorForALEs) {
      return false
    }
    // If employer is ALE, check if the only error is the affordability error
    // we still want to display the error, but allow for the allowance to be created still
    const allowanceErrors = errors.allowances?.[index]

    // Check if there are any other errors besides the employee amount error
    const hasStateError = !!allowanceErrors?.class?.location_id
    const hasSpouseError = !!allowanceErrors?.flat_config?.spouse_amount
    const hasDependentError = !!allowanceErrors?.flat_config?.dependent_amount
    const hasOtherErrors = hasStateError || hasSpouseError || hasDependentError
    if (hasOtherErrors) {
      return false
    }

    // Only bypass if there is only one error and it is the affordability error from validateEmployeeAllowance
    const employeeAmountError = allowanceErrors?.flat_config?.employee_amount?.message
    const isOnlyAffordabilityError = typeof employeeAmountError === 'string' && ACCEPTABLE_ALE_ERRORS_REGEX.test(employeeAmountError)

    return isOnlyAffordabilityError
  }, [employerIsALE, index, onAdd, bypassEmployeeAllownaceErrorForALEs])

  const onClickAddAllowance = async () => {
    const isValid = await trigger(`allowances.${index}`)

    if (isValid || isAcceptableError(errors)) {
      setIsOpen(false)
      onAdd()
    }
  }

  return (
    <CollapsibleContainer
      heading={<AllowanceClassHeader allowance={allowance} />}
      headingIconContent={
        <div className='flex items-center gap-2'>
          {!isExploreMode && (
            <>
              <IconButton
                icon={Pencil}
                label='Edit class'
                onClick={() => setIsOpen(true)}
              />
              <IconButton
                icon={Trash}
                label='Remove class'
                onClick={onRemove}
              />
            </>
          )}
        </div>
      }
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <div className='flex flex-col flex-1 gap-6 p-3'>
        {!isExploreMode && (
          <SlabStateDropdown<Allowances>
            fieldId={`allowances.${index}.class.location_id`}
            fieldLabel='State'
            fieldOptions={stateSelectOptions}
            register={register}
            validationRule={{ required: 'State is required.' }}
            error={errors.allowances?.[index]?.class?.location_id}
          />
        )}
        <AllowanceInputBlock
          label='Allowances'
          description={`This allowance structure will only be applied to employees in ${allowance.class?.location_id ? getFullName(allowance.class.location_id) : 'selected state'}`}
          formIndex={index}
          readOnly={isExploreMode}
          validateEmployeeAllowance={validateEmployeeAllowance}
        />
        {!isExploreMode && (
          <div>
            <ButtonGroup>
              <Button
                variant='tertiary-destructive'
                onClick={onRemove}
                disabled={isSubmitting}
              >
                Remove allowance
              </Button>
              <Button
                variant='secondary'
                onClick={onClickAddAllowance}
                disabled={isSubmitting}
              >
                Create allowance
              </Button>
            </ButtonGroup>
          </div>
        )}
      </div>
    </CollapsibleContainer>
  )
}

export default AllowanceClass
